import { t } from "@travily/i18n";
import type DestinationsIndex from "@travily/types/pages/DestinationsIndex";
import type DestinationSearchHit from "@travily/types/pending/models/DestinationSearchHit";
import { Icon } from "@ui/components/Icon";
import type { WhereSelection } from "@ui/components/SearchBar";
import { SortMenu } from "@ui/components/SortMenu";
import { useCallback, useMemo } from "react";
import { DestinationCard } from "@/components/DestinationCard/DestinationCard";
import { DestinationCardSkeleton } from "@/components/DestinationCard/DestinationCardSkeleton";
import {
  resolveDestinationPrice,
  resolveDestinationTemperature,
} from "@/components/DestinationCard/resolve-destination";
import { type SearchMapPinsOptions, SearchMapSection } from "@/components/Search/SearchMapSection";
import { SearchResultsBody } from "@/components/Search/SearchResultsBody";
import { SearchResultsHeading } from "@/components/Search/SearchResultsHeading";
import { useSearchResults } from "@/hooks/use-search-results";
import type { Bbox } from "@/lib/where-bounding-boxes";
import type { WhereFilter } from "@/lib/where-regions";
import type { MapCluster } from "@/types/search-map";
import { DestinationMapPinCard } from "./components/DestinationMapPinCard";
import { DestinationHitCard } from "./components/DestinationsSearch/DestinationHitCard";
import { RankingExplainer, type RankingTier } from "./components/RankingExplainer";
import type { DestinationFilters } from "./DestinationsFiltersAdapter";
import { useDestinationsSort } from "./DestinationsSortContext";
import { useDestinationAnalytics } from "./useDestinationAnalytics";
import { SCORING_PAYLOAD_KEY } from "./useDestinationsSearch";

// `_source` fields pulled for single-listing map pins — enough for the pin popup card. Hoisted so
// the reference stays stable across renders (it's a `useSearchMapPins` effect dependency).
const DESTINATION_PIN_SOURCE_FIELDS = [
  "id",
  "name",
  "slug",
  "country_slug",
  "country_code",
  "marketing_sentence",
  "cover_image",
  "photos",
  "cost_tier",
  "cost_total_cents",
  "cost_currency",
  "cost_evidence_grade",
  "cost_coverage",
  "location",
];

// Static half of the map-pins options; the scoring block is merged in per
// render (see `pins` below).
const DESTINATION_PINS = {
  host: "/api/destinations",
  index: "destinations",
  geoField: "location",
  sourceFields: DESTINATION_PIN_SOURCE_FIELDS,
} as const;

const getDestinationHitId = (hit: DestinationSearchHit) => hit.id;

// Single-listing pins render as a name pill (every destination has a name; price isn't useful on
// the pin itself and lives in the popup card instead).
const destinationPinLabel = (cluster: MapCluster<DestinationSearchHit>) => cluster.hit?.name ?? null;

// Matches the "Travily Score" sort key. Rank badges are only meaningful under
// the composite-score sort; price sorts hide them so the displayed ordinal
// doesn't suggest a quality ranking.
const DEFAULT_SORT_VALUE = "destinations";

interface DestinationsSearchAdapterProps {
  filters: DestinationFilters;
  rankingTiers: RankingTier[];
  rankingVersion: string;
  /** The scoring block the search sends, mirrored onto the map's own request. */
  scoring: Record<string, unknown>;
  /** Server-computed default first page (see Web::Concerns::SeedResults). */
  seedDestinations?: DestinationsIndex["seed_results"];
  /** The default search's total, seeded so the H1 count is server-rendered. */
  seedTotal?: DestinationsIndex["seed_total"];
  getFavorited?: (type: string, id: string | number) => boolean;
  onFavoriteToggle?: (type: string, id: string | number) => void;
  whereValue: WhereSelection | null;
  whereBoundsBox: string | null;
  whereBbox: Bbox | null;
  /** Term narrowing for the Where selection (region slug / country code). */
  whereFilter: WhereFilter | null;
  markMapArea: (label: string) => void;
}

export function DestinationsSearchAdapter({
  filters,
  getFavorited,
  onFavoriteToggle,
  rankingTiers,
  rankingVersion,
  scoring,
  seedDestinations,
  seedTotal,
  whereValue,
  whereBoundsBox,
  whereBbox,
  whereFilter,
  markMapArea,
}: DestinationsSearchAdapterProps) {
  // The map plots a server-side geo aggregation through its own fetch, which
  // never passes the transporter that injects the scoring block into a search.
  // Without this the pins answer an unfiltered question: a budget could empty
  // the list while the map still showed the places it had just excluded.
  const pins = useMemo<SearchMapPinsOptions>(
    () => ({ ...DESTINATION_PINS, extraBody: { [SCORING_PAYLOAD_KEY]: scoring } }),
    [scoring],
  );
  const results = useSearchResults<DestinationSearchHit>({ sessionKey: "destinations" });
  const sort = useDestinationsSort();
  const { trackResultClicked, trackResultSaved } = useDestinationAnalytics({
    effectiveMonths: filters.effectiveMonths,
    hits: results.hits,
    isLoading: results.isInitialLoading || results.isLoadingMore,
    rankingVersion,
    selectedBands: filters.selectedBands,
    sort: sort.currentValue,
    totalCount: results.totalCount,
  });

  const renderPin = useCallback(
    (hit: DestinationSearchHit, close: () => void, fullWidth = false) => (
      <DestinationMapPinCard
        favorited={getFavorited?.("Destination", hit.id) ?? false}
        fullWidth={fullWidth}
        hit={hit}
        onClose={close}
        onFavoriteToggle={onFavoriteToggle ? () => onFavoriteToggle("Destination", hit.id) : undefined}
      />
    ),
    [getFavorited, onFavoriteToggle],
  );

  const showLive = !(results.isInitialLoading && results.hits.length === 0);
  const count = showLive ? results.totalCount : (seedTotal ?? null);
  const heading = count === null ? null : t("components.views.destinations.search.heading", { count });
  const showRank = sort.currentValue === DEFAULT_SORT_VALUE;
  // Seed cards mirror the live hit cards for the default state: same rank
  // badges (the seed IS the default ranking) and the current-month average
  // temperature the weather trait carries.
  const seedCards = seedDestinations?.map((destination, index) => {
    const { temperature, weatherIcon } = resolveDestinationTemperature(destination.current_avg_temp_celsius);
    return (
      <DestinationCard
        destination={destination}
        favorited={getFavorited?.("Destination", destination.id) ?? false}
        key={destination.id}
        onFavoriteToggle={onFavoriteToggle ? () => onFavoriteToggle("Destination", destination.id) : undefined}
        price={resolveDestinationPrice(destination)}
        priceGrade={destination.cost_evidence_grade}
        rank={index + 1}
        showDescription
        temperature={temperature}
        theme="compact"
        weatherIcon={weatherIcon}
      />
    );
  });

  return (
    <SearchMapSection
      getHitId={getDestinationHitId}
      heading={
        <SearchResultsHeading
          actions={
            <div className="flex items-center gap-2">
              <RankingExplainer
                budget={filters.budgetCeiling}
                tiers={rankingTiers}
                datesNamed={Boolean(filters.whenSelection.start)}
                windowMonths={filters.effectiveMonths}
              />
              {/* Desktop sort lives in the SearchBar (see index.tsx); mobile keeps it here. */}
              <div className="lg:hidden">
                <SortMenu currentValue={sort.currentValue} onChange={sort.refine} options={sort.options} size="36" />
              </div>
            </div>
          }
          heading={heading}
        />
      }
      headingLabel={heading ?? t("components.frames.app_layout.nav.destinations")}
      sheetHeading={
        <SearchResultsHeading
          actions={
            <SortMenu
              currentValue={sort.currentValue}
              onChange={sort.refine}
              options={sort.options}
              size="36"
              variant="icon"
            />
          }
          heading={heading}
          variant="sheet"
        />
      }
      mapAreaLabel={t("components.views.destinations.search.map_area")}
      mapEmptyLabel={t("components.views.destinations.search.empty_title")}
      markMapArea={markMapArea}
      pinIcon={<Icon name="map" size="32" type="sticker" />}
      pinLabel={destinationPinLabel}
      pins={pins}
      renderPin={renderPin}
      whereBbox={whereBbox}
      whereBoundsBox={whereBoundsBox}
      whereFilter={whereFilter}
      whereValue={whereValue}
    >
      <SearchResultsBody
        emptyDescription={t("components.views.destinations.search.empty_description")}
        emptyTitle={t("components.views.destinations.search.empty_title")}
        getKey={(hit) => `${hit.country_slug}/${hit.slug}`}
        illustration="trips"
        renderCard={(hit, index) => (
          <DestinationHitCard
            effectiveMonths={filters.effectiveMonths}
            favorited={getFavorited?.("Destination", hit.id) ?? false}
            hit={hit}
            onFavoriteToggle={
              onFavoriteToggle
                ? () => {
                    trackResultSaved(hit, index + 1, !(getFavorited?.("Destination", hit.id) ?? false));
                    onFavoriteToggle("Destination", hit.id);
                  }
                : undefined
            }
            onPress={() => trackResultClicked(hit, index + 1)}
            rank={showRank ? index + 1 : undefined}
          />
        )}
        renderSkeletonCard={(key) => <DestinationCardSkeleton key={key} withDescription />}
        results={results}
        seedCards={seedCards}
        skeletonCount={12}
      />
    </SearchMapSection>
  );
}
